'use client'

import { useState, useMemo } from 'react'

import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'

import { LevelFooter } from '../../Footer'
import { HUD } from '../../HUD'
import { LevelToolbar } from '../../Toolbar'

import { useLevelContext } from '@/features/level-play'
import {
  getIncompleteSelectionMessage,
  getMultipleChoiceSelectionStatus
} from '@/features/level-play/domain/getMultipleChoiceSelectionStatus'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'


import { QuestionMultipleChoice } from './MultipleChoice'
import { QuestionQRCode } from './QRCode'
import { QuestionText } from './Text'

interface GameQuestionProps {
  step: ChallengeStep
  challengeId: number
}

export function GameQuestion({ challengeId }: GameQuestionProps) {
  const {
    state,
    interactionDisabled,
    goNext,
    enableContinue,
    verifyChallenge,
    retryChallenge
  } = useFlow()
  const { level, journeyAllowWrongAnswers, isSubmitting, onChallengeAnswer } =
    useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const meta = challenge?.meta
  const questionKind = meta?.question_kind || 'text'
  const normalizedAlternatives = useMemo(() => {
    const rawAlternatives =
      challenge?.challenge_options ?? meta?.alternatives ?? []
    return (rawAlternatives || [])
      .map((alternative: any) => {
        if (typeof alternative === 'string') {
          return { name: alternative, is_correct: false }
        }
        const name =
          alternative?.name ??
          alternative?.text ??
          alternative?.label ??
          alternative?.value ??
          ''
        if (!name) return null
        const is_correct =
          alternative?.meta?.is_correct ?? alternative?.is_correct ?? false
        return { name, is_correct }
      })
      .filter(
        (alternative): alternative is { name: string; is_correct: boolean } =>
          alternative !== null
      )
  }, [challenge?.challenge_options, meta?.alternatives])

  const [selectedAnswer, setSelectedAnswer] = useState<
    string | string[] | null
  >(null)
  const [incompleteSelectionMessage, setIncompleteSelectionMessage] = useState<
    string | null
  >(null)

  const selectionStatus = useMemo(
    () =>
      getMultipleChoiceSelectionStatus(normalizedAlternatives, selectedAnswer),
    [normalizedAlternatives, selectedAnswer]
  )

  const handleSelectAnswer = (answer: string | string[] | null) => {
    setIncompleteSelectionMessage(null)
    setSelectedAnswer(answer)
  }

  const validateAnswer = (
    answer: string | string[] | null,
    metaField: any,
    alternatives: { name: string; is_correct?: boolean }[]
  ) => {
    if (!answer) return false

    const { answer: correctAnswer = '' } = metaField ?? {}
    let expectedAnswer = correctAnswer

    if (questionKind === 'multiple_choice') {
      expectedAnswer = alternatives
        .filter(alt => alt.is_correct)
        .map(alt => alt.name)
        .sort()
    }

    if (Array.isArray(answer)) {
      answer = answer.sort()
    }

    const normalizedExpected = expectedAnswer
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    const normalizedAnswer = answer
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    return normalizedExpected === normalizedAnswer
  }

  const handleVerify = async () => {
    if (!selectedAnswer) return

    // Selecao incompleta nao vira resposta: sem submissao, sem perda de vida,
    // so o aviso de que ainda faltam alternativas a marcar.
    if (questionKind === 'multiple_choice' && !selectionStatus.isComplete) {
      setIncompleteSelectionMessage(
        getIncompleteSelectionMessage(selectionStatus.missingCount)
      )
      return
    }

    setIncompleteSelectionMessage(null)

    const answerIsCorrect = validateAnswer(
      selectedAnswer,
      meta,
      normalizedAlternatives
    )
    const skipValidation = meta?.save_answer
    const canAnswerChallenge =
      answerIsCorrect || skipValidation || journeyAllowWrongAnswers

    try {
      if (canAnswerChallenge) {
        await onChallengeAnswer({
          uid: challenge.uid,
          answer: Array.isArray(selectedAnswer)
            ? selectedAnswer.join(', ')
            : selectedAnswer || '',
          answerIsCorrect: answerIsCorrect || skipValidation,
          skipValidation
        })
        enableContinue()
      }
      verifyChallenge({ correct: answerIsCorrect || skipValidation })
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const handleRetry = () => {
    setSelectedAnswer(null)
    setIncompleteSelectionMessage(null)
    retryChallenge()
  }

  const hasNoAlternatives =
    questionKind === 'multiple_choice' && normalizedAlternatives.length === 0

  const handleSkipEmptyQuestion = async () => {
    try {
      await onChallengeAnswer({
        uid: challenge.uid,
        answer: '',
        answerIsCorrect: true,
        skipValidation: true
      })
      enableContinue()
      goNext()
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const hasAnswer =
    selectedAnswer &&
    (Array.isArray(selectedAnswer)
      ? selectedAnswer.length > 0
      : selectedAnswer.trim() !== '')

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        {questionKind === 'multiple_choice' && (
          <QuestionMultipleChoice
            alternatives={normalizedAlternatives}
            shuffle={meta?.shuffle}
            selectedAnswer={selectedAnswer}
            onSelect={handleSelectAnswer}
            disabled={interactionDisabled}
            state={state}
          />
        )}

        {questionKind === 'text' && (
          <QuestionText
            value={selectedAnswer as string}
            onChange={handleSelectAnswer}
            disabled={interactionDisabled}
          />
        )}

        {questionKind === 'qr_code' && (
          <QuestionQRCode
            onScan={code => handleSelectAnswer(code)}
            disabled={interactionDisabled}
          />
        )}

        {incompleteSelectionMessage && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center text-sm font-medium text-amber-800"
          >
            {incompleteSelectionMessage}
          </div>
        )}
      </HUD.Body>

      <LevelFooter>
        {state.phase === 'READY' && hasNoAlternatives && (
          <Button
            type="button"
            onClick={handleSkipEmptyQuestion}
            className="w-full font-bold btn btn-info"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>Continuar</span>
            )}
          </Button>
        )}

        {state.phase === 'READY' && !hasNoAlternatives && (
          <Button
            type="button"
            onClick={handleVerify}
            className="w-full font-bold btn btn-info"
            disabled={!hasAnswer || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>Verificar</span>
            )}
          </Button>
        )}

        {state.phase === 'CORRECT' && (
          <Button
            type="button"
            onClick={goNext}
            className="w-full font-bold btn btn-info"
            disabled={isSubmitting}
          >
            <span>Continuar</span>
          </Button>
        )}

        {state.phase === 'LOCKED_WRONG' && (
          <Button
            type="button"
            onClick={journeyAllowWrongAnswers ? goNext : handleRetry}
            className="w-full font-bold btn btn-info"
            disabled={isSubmitting}
          >
            <span>
              {journeyAllowWrongAnswers ? 'Entendido' : 'Tentar novamente'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}
